import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { formatWholeNumber } from '../game/util/numberFormat';

function DivisionCalculation({ calculation, isActive, loweredTargetRef }) {
  const cellStyle = (value, endIndex) => {
    const length = String(value).length;
    return {
      gridColumn: Math.max(1, endIndex - length + 2) + ' / span ' + length,
    };
  };

  const digitRowStyle = {
    gridColumn: '1 / -1',
    gridTemplateColumns: 'repeat(var(--tmag-division-columns), var(--cell))',
  };

  const renderDigits = (value, endIndex, withMinus = false, loweredDigitIndex = null) => {
    const digits = String(value).split('');
    const firstColumn = Math.max(1, endIndex - digits.length + 2);
    return digits.map((digit, index) => (
      <span
        className={'tmag-division-column-digit' + (index === loweredDigitIndex ? ' is-lowered-digit' : '')}
        style={{ gridColumn: firstColumn + index, position: 'relative' }}
        ref={index === loweredDigitIndex ? loweredTargetRef : undefined}
        key={firstColumn + '-' + index}
      >
        {withMinus && index === 0 ? <i className="tmag-division-minus">−</i> : null}
        {digit}
      </span>
    ));
  };

  return (
    <div className={'tmag-division-calculation' + (isActive ? ' is-active' : '')}>
      <div className="tmag-division-product-row" style={digitRowStyle}>
        {renderDigits(calculation.product, calculation.endIndex, calculation.stage === 'subtract')}
      </div>
      {calculation.stage === 'subtract' ? (
        <>
          <div className="tmag-division-subtraction-line" style={cellStyle(calculation.product, calculation.endIndex)} />
          <div className="tmag-division-remainder-row" style={digitRowStyle}>
            {calculation.broughtValue !== null
              ? renderDigits(
                calculation.broughtValue,
                calculation.endIndex + 1,
                false,
                isActive && Number.isInteger(calculation.broughtColumn)
                  ? String(calculation.broughtValue).length - 1
                  : null,
              )
              : renderDigits(calculation.remainder, calculation.endIndex)}
          </div>
        </>
      ) : null}
    </div>
  );
}

function DivisionAlgorithmVisual({ tutorial, step }) {
  const algorithmRef = useRef(null);
  const loweredSourceRef = useRef(null);
  const loweredTargetRef = useRef(null);
  const [dropGuide, setDropGuide] = useState(null);
  const dividendDigits = tutorial.dividendDigits.split('');
  const quotientCells = step.quotientCells || [];
  const activeIndexes = new Set(step.activeIndexes || []);
  const loweringCalculation = (step.calculations || []).find(calculation =>
    calculation.key === step.activeCalculation && Number.isInteger(calculation.broughtColumn),
  );
  const loweredSourceIndex = loweringCalculation?.broughtColumn ?? null;
  const extendedAlgorithm = tutorial.width >= 3;
  const groupedDigits = Boolean(tutorial.formatLargeNumbers && tutorial.width >= 4);
  const showCommaAfter = (digitCount, index) =>
    groupedDigits && index < digitCount - 1 && (digitCount - index - 1) % 3 === 0;
  const quotientStartIndex = quotientCells.findIndex(digit => digit !== '');
  const quotientLength = quotientStartIndex === -1 ? 0 : quotientCells.length - quotientStartIndex;

  useLayoutEffect(() => {
    const container = algorithmRef.current;
    const source = loweredSourceRef.current;
    const target = loweredTargetRef.current;
    if (!container || !source || !target) {
      setDropGuide(null);
      return undefined;
    }

    const measure = () => {
      const containerBox = container.getBoundingClientRect();
      const sourceBox = source.getBoundingClientRect();
      const targetBox = target.getBoundingClientRect();
      setDropGuide({
        width: containerBox.width,
        height: containerBox.height,
        startX: sourceBox.left - containerBox.left + sourceBox.width / 2,
        startY: sourceBox.bottom - containerBox.top + 3,
        endX: targetBox.left - containerBox.left + targetBox.width / 2,
        endY: targetBox.top - containerBox.top - 3,
      });
    };

    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(container);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [step.id, loweringCalculation?.key]);

  return (
    <div
      ref={algorithmRef}
      className={'tmag-division-algorithm' + (extendedAlgorithm ? ' is-extended' : '') + (tutorial.hasTwoDigitDivisor ? ' has-two-digit-divisor' : '')}
      style={{ '--tmag-division-columns': tutorial.width }}
    >
      <div className="tmag-division-quotient-label">Cociente</div>
      <div className="tmag-division-quotient-row">
        {quotientCells.map((digit, index) => {
          const quotientPosition = index - quotientStartIndex;
          return (
            <span className={step.activeQuotientIndex === index ? 'is-active is-new' : ''} key={'q-' + index}>
              {digit}
              {digit !== '' && showCommaAfter(quotientLength, quotientPosition)
                ? <i className="tmag-division-digit-comma">,</i>
                : null}
            </span>
          );
        })}
      </div>

      <div className="tmag-division-body">
        <span className={'tmag-division-divisor' + (step.activeDivisor ? ' is-active' : '')}>
          {formatWholeNumber(tutorial.divisor)}
        </span>
        <div className="tmag-division-bracket">
          <div className="tmag-division-dividend">
            {dividendDigits.map((digit, index) => (
              <span
                ref={index === loweredSourceIndex ? loweredSourceRef : undefined}
                className={activeIndexes.has(index) ? 'is-active' : ''}
                key={'d-' + index}
              >
                {digit}
                {showCommaAfter(dividendDigits.length, index)
                  ? <i className="tmag-division-digit-comma">,</i>
                  : null}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="tmag-division-calculations">
        {(step.calculations || []).map(calculation => (
          <DivisionCalculation
            calculation={calculation}
            isActive={step.activeCalculation === calculation.key}
            loweredTargetRef={step.activeCalculation === calculation.key ? loweredTargetRef : undefined}
            key={calculation.key}
          />
        ))}
      </div>
      {dropGuide ? (
        <svg
          className="tmag-division-drop-guide"
          viewBox={'0 0 ' + dropGuide.width + ' ' + dropGuide.height}
          aria-hidden="true"
        >
          <defs>
            <marker id="tmag-division-drop-arrowhead" markerWidth="7" markerHeight="7" refX="5.5" refY="3.5" orient="auto">
              <path d="M0,0 L7,3.5 L0,7 Z" />
            </marker>
          </defs>
          <path
            d={'M ' + dropGuide.startX + ' ' + dropGuide.startY + ' L ' + dropGuide.endX + ' ' + dropGuide.endY}
            markerEnd="url(#tmag-division-drop-arrowhead)"
          />
        </svg>
      ) : null}
    </div>
  );
}

function DivisionGroupsVisual({ tutorial, step }) {
  return (
    <div className="tmag-division-visual-summary">
      <div className="tmag-division-visual-equation" aria-label={tutorial.dividend + ' dividido entre ' + tutorial.divisor + ' es ' + tutorial.quotient}>
        {formatWholeNumber(tutorial.dividend)} <span>÷</span> {formatWholeNumber(tutorial.divisor)} <span>=</span> {formatWholeNumber(tutorial.quotient)}
      </div>
      <div className="tmag-division-groups" aria-label={step.message}>
        {step.groups.map(group => (
          <div className="tmag-division-group" key={group.label}>
            <span>Grupo {group.label}</span>
            <div className="tmag-division-gems" aria-label={group.count + ' gemas'}>
              {group.gems.map(gem => <i className="tmag-division-gem" key={gem} aria-hidden="true" />)}
            </div>
            <strong>{formatWholeNumber(group.count)} en cada grupo</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DivisionWorkedExampleTutorial({ tutorial, onComplete }) {
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    const lastStep = Math.max(0, (tutorial?.tutorial?.steps?.length || 1) - 1);
    const requestedStep = Number.isInteger(tutorial?.initialStepIndex)
      ? tutorial.initialStepIndex
      : 0;
    setStepIndex(Math.min(Math.max(0, requestedStep), lastStep));
  }, [tutorial?.sessionId]);

  const step = tutorial?.tutorial?.steps?.[stepIndex];
  const finalStep = Boolean(tutorial && stepIndex === tutorial.tutorial.steps.length - 1);
  const progress = useMemo(
    () => tutorial ? (stepIndex + 1) + ' de ' + tutorial.tutorial.steps.length : '',
    [tutorial, stepIndex],
  );

  if (!tutorial || !step) return null;

  return (
    <section className="tmag-worked-overlay tmag-division-theme" role="dialog" aria-modal="true" aria-labelledby="division-worked-example-title">
      <div className="tmag-worked-dialog">
        <header className="tmag-worked-header">
          <span className="tmag-worked-orb" aria-hidden="true">◈</span>
          <div>
            <p>{progress}</p>
            <h2 id="division-worked-example-title">VEAMOS CÓMO SE HACE</h2>
          </div>
        </header>

        <div className="tmag-worked-step">
          <span className="tmag-worked-step-badge">{step.label}</span>
          <h3>{step.title}</h3>
          <p>{step.message}</p>
          {step.isVisualRepresentation
            ? <DivisionGroupsVisual tutorial={tutorial.tutorial} step={step} />
            : <DivisionAlgorithmVisual tutorial={tutorial.tutorial} step={step} />}
        </div>

        <button
          className={'tmag-worked-next' + (finalStep ? ' is-final' : '')}
          type="button"
          onClick={() => finalStep ? onComplete() : setStepIndex(current => current + 1)}
        >
          {finalStep ? 'SEGUIR PRACTICANDO  →' : 'SIGUIENTE  →'}
        </button>
      </div>
    </section>
  );
}

export default DivisionWorkedExampleTutorial;
